import React, {useEffect, useState} from "react";
import {Stack} from "@mui/material";
import {v4 as uuidv4} from "uuid";
import {useSearchParams} from "react-router-dom";
import {NodeEntity, NodeType, ScopeType, TargetType} from "../../../../model/ModelData";
import {EditNodeMenu} from "./EditNodeMenu";
import {sortFormNodes} from "./formNodesSort";
import {FormNodesPanel} from "./components/FormNodesPanel";

// Re-exported for existing importers.
export {sortFormNodes} from "./formNodesSort";

export const EditNodeMenuWrapper = ({
                                        open,
                                        selectedNode,
                                        nodes,
                                        onClose,
                                        onUpdate,
                                        onUpdateNodes,
                                        onDuplicate,
                                        onRemove,
                                        project,
                                        flow,
                                        onRemoveForm,
                                    }) => {
    const [node, setNode] = useState(null);
    const [items, setItems] = useState([]);
    const [searchParams] = useSearchParams();
    const routeNode = searchParams.get("node");
    const selectedRoute = searchParams.get("route");

    useEffect(() => {
        if (selectedNode) {
            let newItems = [];
            if (selectedNode?.data?.form_id) {
                newItems = nodes?.filter(i => i?.data?.form_id === selectedNode?.data?.form_id);
                if (selectedNode?.data?.form_id !== node?.data?.form_id) {
                    setNode(null);
                }
            } else {
                setNode(selectedNode);
            }
            setItems(newItems);
        }
    }, [selectedNode, nodes]);

    useEffect(() => {
        if (routeNode && nodes?.length) {
            const newNode = nodes?.find(i => i?.id === routeNode);
            setNode(newNode || null);
        }
    }, [nodes, routeNode]);

    const createNode = (type) => {
        const newNodes = [...nodes];
        const startNode = items.find(i => i.data?.form_id === selectedNode?.data?.form_id && i.type === NodeType.START_FORM);
        if (!startNode) {
            return;
        }
        const formNodes = sortFormNodes(startNode, [...items]);

        const prevNode = formNodes[formNodes.length - 2];
        const endNode = formNodes[formNodes.length - 1];

        const newNode = {
            ...NodeEntity,
            id: uuidv4(),
            name: type?.toLowerCase()?.replaceAll("_", " "),
            type: type, // NodeType
            coords: {x: 0, y: 0},
            parameters: [],
            data: {
                greet: [],
                temp: 1.0,
                form_id: selectedNode?.data?.form_id,
            },
            routes: {
                static: {
                    next: {
                        id: uuidv4(),
                        target: endNode.id,
                        name: endNode.name,
                        type: "next",
                        backstack: false,
                        data: {
                            greet: "",
                        },
                    },
                    no_match: null,
                    fail: null,
                },
                dynamic: [],
            },
        };
        if (type === NodeType.HOOK) {
            newNode.data.api_url = "";
            newNode.data.api_method = "GET";
            newNode.data.queryParams = [];
            newNode.data.bodyParams = [];
            newNode.data.target = TargetType.FLOW;
            newNode.data.params = [{id: uuidv4(), source: null, scope: ScopeType.FLOW}];
        } else if (type === NodeType.QA || type === NodeType.RECOMMENDATION) {
            newNode.data.qa_method = project?.settings?.qa_method || "";
            newNode.data.qa_api = project?.settings?.qa_api || "";
            newNode.data.please_wait_messages = [];
            newNode.data.persona = "";
            newNode.data.slots = [];
            newNode.data.paraphrazer = true;
            newNode.data.lemmatizer = true;
            newNode.data.conversation_buffer_window = 4;
            if (type === NodeType.RECOMMENDATION) {
                newNode.data.parser = `
        function parser(result) {
          // Write your code here
        }
        `;
            }
        } else if (type === NodeType.QUERY) {
            node.data.global_intents_included = true;
        }
        prevNode.routes.static.next.target = newNode.id;

        newNodes.push(newNode);
        return newNodes.map(n => {
            if (n.id === prevNode.id) {
                return prevNode;
            } else if (n.id === endNode.id) {
                return endNode;
            }
            return n;
        });
    };

    const addNode = async (type) => {
        const newNodes = createNode(type);
        await onUpdateNodes([...newNodes]);
        let newFormNodes = [];
        if (nodes) {
            newFormNodes = newNodes?.filter(n => n?.data?.form_id === selectedNode?.data?.form_id);
        }
        setItems(newFormNodes);
    };

    const onUpdateMultipleNodes = async (updateNodes) => {
        let newNodes = [...nodes];
        newNodes = newNodes.map(n => {
            const finded = updateNodes.find(i => i?.id === n.id);
            return finded || n;
        });
        await onUpdateNodes([...newNodes]);
    };

    if (!open || !selectedNode) return null;

    return (
        <>
            {selectedNode?.data?.form_id && items.length > 0 && (
                <Stack direction="column" sx={{width: 280, height: "100%", maxHeight: "100%"}}>
                    <FormNodesPanel
                        items={items}
                        node={selectedNode}
                        value={node}
                        setValue={setNode}
                        onRemove={onRemoveForm}
                        onClose={() => {
                            onClose();
                            setNode(null);
                        }}
                        addNode={addNode}
                        onUpdateNodes={onUpdateMultipleNodes}
                    />
                </Stack>
            )}
            {node && (
                <EditNodeMenu
                    open={!!node}
                    selectedNode={node}
                    flow={flow}
                    project={project}
                    nodes={nodes}
                    selectedRoute={selectedRoute}
                    onDuplicate={onDuplicate}
                    showHideMenuButton={node?.id === selectedNode?.id && items.length === 0}
                    onClose={() => {
                        onClose();
                        setNode(null);
                    }}
                    onUpdate={async (newNode) => {
                        if (node !== newNode) {
                            await onUpdate(newNode);
                            setNode(newNode);
                        }
                    }}
                    onRemove={onRemove}
                />
            )}
        </>
    );
};
